/**
 * The flamethrower officer (02 §9.1): closes to 7 m and plays a jet over the ranger - one
 * wound for every 0.75 s he stays in it. The tank on his back is his weakness: a round from
 * behind, or any blast, and it bursts in a 3 m ball of fire that kills him and anyone beside him.
 *
 * Fields, for the renderer and audio: `peeking` = the jet is lit; `burstLeft` = ticks the
 * ranger has been in it since the last wound. The tank itself is `world.hazards.fuelTanks`.
 */

import { makeNoise } from '../../alert';
import type { Ctx } from '../../ctx';
import { explode, woundRanger } from '../../damage';
import { angleDiff, atan2, PI } from '../../math';
import { clearSight } from '../../smoke';
import { ENEMY, SPECIALS } from '../../tuning';
import type { Enemy } from '../../types';
import { travel, turnToward } from '../movement';
import { combatPreamble } from './common';
import { tankOf } from './kinds';

const DEG = PI / 180;

export function stepFlameCombat(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  const r = w.ranger;
  const dx = r.x - e.x;
  const dz = r.z - e.z;
  const dist = Math.sqrt(dx * dx + dz * dz);
  const bearing = atan2(dx, dz);
  let burning = false;

  if (!combatPreamble(ctx, e) && e.seesRanger) {
    // The tank is heavy: he comes round slowly, which is the chance to get behind him.
    if (dist > SPECIALS.flameRange) travel(ctx, e, r.x, r.z, ENEMY.huntSpeed, SPECIALS.flameRange - 1);
    else {
      e.moving = false;
      turnToward(e, bearing, SPECIALS.flameTurnRate);
    }
    burning = dist <= SPECIALS.flameRange && e.suppressTicks <= 0
      && Math.abs(angleDiff(e.facing, bearing)) <= (SPECIALS.flameConeDeg / 2) * DEG
      && clearSight(ctx, e.x, e.z, 1.3, r.x, r.z, r.stance === 'crawl' ? 0.4 : 1.2);
  }

  e.peeking = burning;
  if (!burning) {
    e.burstLeft = Math.max(0, e.burstLeft - 1);
    return;
  }
  e.sinceShot = 0;
  // The roar carries: the garrison hears a fight.
  if (e.burstLeft === 0) makeNoise(ctx, e.x, e.z, SPECIALS.flameNoise, 'gunfire', e.id);
  e.burstLeft++;
  if (e.burstLeft >= SPECIALS.flameWoundTicks) {
    e.burstLeft = 0;
    ctx.events.push({ type: 'rangerHit', wounded: true });
    woundRanger(ctx, 1, 'fire');
  }
}

/**
 * Every tick, for every flamethrower, alive or not: has his tank been holed from behind, or
 * has a blast lit its fuse (`hazards/blast.ts`)? Runs from `stepHazards`, because the hit that
 * matters may be the one that killed him, and dead men are not stepped.
 */
export function stepFuelTanks(ctx: Ctx): void {
  const w = ctx.world;
  const r = w.ranger;
  for (const e of w.enemies) {
    if (e.kind !== 'flamethrower') continue;
    const tank = tankOf(w.hazards, e);
    if (!tank.intact) continue;
    const lit = tank.fuseTicks > 0;
    const cooked = lit && --tank.fuseTicks === 0;
    // A drop in hp is a hit. A bullet's flight is a blink, so where the ranger stands now is where
    // it came from; the knife is not a bullet, and opens no tank. With the fuse already lit the drop
    // is the blast's doing, not a round's: it must not be read as a shot in the back and jump the fuse.
    const hit = !lit && e.hp < tank.hp && r.weapon !== 'knife';
    tank.hp = e.hp;
    const offFacing = Math.abs(angleDiff(e.facing, atan2(r.x - e.x, r.z - e.z)));
    const fromBehind = hit && offFacing >= (180 - SPECIALS.flameRearArcDeg / 2) * DEG;
    if (!cooked && !fromBehind) continue;
    tank.intact = false;
    tank.fuseTicks = -1;
    e.peeking = false;
    // He is at the centre of it: `explode` kills him, and singes whoever stands within 3 m.
    // His kill, but not his grenade: a rupture beside a wall is not told that walls need a LAW.
    explode(ctx, e.x, e.z, SPECIALS.ruptureRadius, 'grenade', true, false);
  }
}
